import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Input, Modal, Typography } from 'antd';
import { MessageOutlined } from '@ant-design/icons';

import { formatInstant } from '../../../../format';
import { GOLD } from '../../../../config/palette.js';
import { NOTE_MAX, appendNotes, itemsOf, notesQuery, olderNotesText } from './statementLines.js';

const { Text } = Typography;

/**
 * The notes read so far, newest first, and - while the server holds older
 * ones (its nextCursor) - how many are shown and Load older (READ-3, CON-4).
 * Rendered by LineNotes, and on its own by bankStatementScreen.test.js.
 *
 * @param {{ notes: object[]|null, more: boolean, total: number, loadingOlder?: boolean, onOlder: () => void }} props
 */
export const NotesList = ({ notes, more, total, loadingOlder = false, onOlder }) => {
    if (!notes) return null;
    if (notes.length === 0) return <Text type="secondary">No notes yet.</Text>;
    return (
        <>
            <ul className="statement-notes-list">
                {notes.map((n) => (
                    <li key={n.noteId}>
                        <div>{n.note}</div>
                        <Text type="secondary" className="statement-notes-by">{(n.notedByName ?? n.notedBy ?? '—') + ' · ' + formatInstant(n.notedAt)}</Text>
                    </li>
                ))}
            </ul>
            {more && (
                <div className="statement-notes-older">
                    <Text type="secondary">{olderNotesText({ shown: notes.length, total, more })}</Text>
                    <Button size="small" type="link" onClick={onOlder} loading={loadingOlder}>Load older</Button>
                </div>
            )}
        </>
    );
};

/**
 * The Comment cell (design 8): how many notes a line has and its last one,
 * from the list; the modal reads them newest first, a page at a time (GET
 * cash/lines/{id}/notes - 50 a page, the server's nextCursor while older ones
 * remain), says so while older notes are not loaded and reads them with Load
 * older (READ-3, CON-4: a line's oldest notes, often its first explanation,
 * are reachable). For whoever claims or approves matches it adds one
 * (LINE_NOTE: {lineId, note}, 1 to 500 characters). A note is not a
 * decision, so it asks for no code; notes are never edited or removed.
 *
 * @param {{ line: object, canNote: boolean, api: object, onAdded: (note: string) => void }} props
 */
const LineNotes = ({ line, canNote, api, onAdded }) => {
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    // The newest page read again (reopened, a note added) outdates an older page still in flight.
    const latest = useRef(0);

    const load = async () => {
        const ticket = ++latest.current;
        setLoading(true);
        setError(null);
        try {
            const page = await api.cash.notes(line.lineId);
            if (ticket !== latest.current) return;
            setNotes(itemsOf(page, 'notes'));
            setNextCursor(page?.nextCursor ?? null);
        } catch (e) {
            if (ticket === latest.current) setError(e.message);
        } finally {
            if (ticket === latest.current) setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
    }, [open, line.lineId]);

    const older = async () => {
        if (!nextCursor) return;
        const ticket = latest.current;
        setLoadingOlder(true);
        setError(null);
        try {
            const page = await api.cash.notes(line.lineId, notesQuery(nextCursor));
            if (ticket !== latest.current) return;
            setNotes((shown) => appendNotes(shown, itemsOf(page, 'notes')));
            setNextCursor(page?.nextCursor ?? null);
        } catch (e) {
            if (ticket === latest.current) setError(e.message);
        } finally {
            setLoadingOlder(false);
        }
    };

    const add = async () => {
        const note = draft.trim();
        if (!note) return;
        setSaving(true);
        setError(null);
        try {
            await api.cash.addNote(line.lineId, { lineId: line.lineId, note });
            setDraft('');
            onAdded(note);
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const content = (
        <div className="statement-notes">
            {error && <Alert type="error" showIcon title={error} className="auth-alert" />}
            {loading && !notes && <Text type="secondary">Loading…</Text>}
            <NotesList notes={notes} more={Boolean(nextCursor)} total={Number(line.noteCount) || 0} loadingOlder={loadingOlder} onOlder={older} />
            {canNote && (
                <div className="statement-notes-add">
                    <Input.TextArea value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={NOTE_MAX} showCount
                        autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Explain this line…" disabled={saving} />
                </div>
            )}
        </div>
    );

    const close = () => {
        setOpen(false);
        setDraft('');
        setError(null);
    };

    return (
        <>
            <Button size="small" type="text" className="statement-notes-button" aria-label={'Notes on ' + (line.referenceId ?? line.lineId)}
                onClick={() => setOpen(true)}>
                <Badge count={Number(line.noteCount) || 0} size="small" color={GOLD.base}><MessageOutlined /></Badge>
                <span className="statement-last-note">{line.lastNote ?? ''}</span>
            </Button>
            <Modal
                open={open}
                onCancel={close}
                title="Notes"
                centered
                width={480}
                footer={[
                    <Button key="close" onClick={close} disabled={saving}>Close</Button>,
                    canNote && (
                        <Button key="add" type="primary" onClick={add} loading={saving} disabled={!draft.trim()}>
                            Add note
                        </Button>
                    )
                ]}
                rootClassName="lfoc-styles-wrapper statement-notes-modal"
                destroyOnHidden
            >
                {content}
            </Modal>
        </>
    );
};

export default LineNotes;
